import type { DesktopIcon, StartMenuItem, Project, BlogPost, Skill } from '../types';

// Taskbar height
export const TASKBAR_HEIGHT = 36;

// Default window sizes
export const DEFAULT_WINDOW_SIZE = { width: 600, height: 400 };
export const DEFAULT_MIN_SIZE = { width: 200, height: 150 };

// App-specific window sizes (for apps that need custom sizes)
export const APP_WINDOW_SIZES: Record<string, { width: number; height: number }> = {
  'minesweeper': { width: 280, height: 380 },
  'solitaire': { width: 700, height: 500 },
  'minecraft': { width: 950, height: 680 },
  'projects': { width: 950, height: 680 },
  'research': { width: 950, height: 680 },
  'my-computer': { width: 950, height: 680 },
  'recycle-bin': { width: 950, height: 680 },
  'about-me': { width: 950, height: 680 },
  'internet-explorer': { width: 950, height: 680 },
  'resume': { width: 950, height: 680 },
  'contact': { width: 950, height: 680 },
  'blog': { width: 1400, height: 900 },
};

// Desktop icon grid
export const ICON_SIZE = 90;
export const ICON_PADDING = 12;

// App icons (SVG names)
export const APP_ICONS: Record<string, string> = {
  'my-computer': 'computer',
  'recycle-bin': 'recycle-bin',
  'projects': 'folder-projects',
  'about-me': 'user',
  'contact': 'mail',
  'resume': 'document',
  'blog': 'notepad',
  'settings': 'settings',
  'notepad': 'notepad',
  'minesweeper': 'minesweeper',
  'solitaire': 'solitaire',
  'internet-explorer': 'internet-explorer',
  'msn-messenger': 'msn-messenger',
  'command-prompt': 'command-prompt',
  'minecraft': 'minecraft',
  'research': 'research',
};

// App titles
export const APP_TITLES: Record<string, string> = {
  'my-computer': 'My Computer',
  'recycle-bin': 'Recycle Bin',
  'projects': 'My Projects',
  'about-me': 'About Me',
  'contact': 'Contact',
  'resume': 'Resume',
  'blog': 'Blog',
  'settings': 'Settings',
  'notepad': 'Notepad',
  'minesweeper': 'Minesweeper',
  'solitaire': 'Solitaire',
  'internet-explorer': 'Internet Explorer',
  'msn-messenger': 'MSN Messenger',
  'command-prompt': 'Command Prompt',
  'minecraft': 'Minecraft',
  'research': 'Research',
};

// External link apps - these open URLs instead of windows
export const EXTERNAL_LINK_APPS: Record<string, string> = {
  // minecraft now uses embedded iframe with conditional source
};

// Default desktop icons
export const DEFAULT_DESKTOP_ICONS: DesktopIcon[] = [
  { id: 'my-computer', name: 'My Computer', icon: 'computer', appType: 'my-computer', position: { x: 0, y: 0 } },
  { id: 'recycle-bin', name: 'Recycle Bin', icon: 'recycle-bin', appType: 'recycle-bin', position: { x: 0, y: 1 } },
  { id: 'projects', name: 'My Projects', icon: 'folder-projects', appType: 'projects', position: { x: 0, y: 2 } },
  { id: 'about-me', name: 'About Me', icon: 'user', appType: 'about-me', position: { x: 0, y: 3 } },
  { id: 'internet-explorer', name: 'Internet Explorer', icon: 'internet-explorer', appType: 'internet-explorer', position: { x: 0, y: 4 } },
  { id: 'resume', name: 'Resume', icon: 'document', appType: 'resume', position: { x: 0, y: 5 } },
  { id: 'contact', name: 'Contact', icon: 'mail', appType: 'contact', position: { x: 0, y: 6 } },
  { id: 'minecraft', name: 'Minecraft', icon: 'minecraft', appType: 'minecraft', position: { x: 1, y: 0 } },
  { id: 'research', name: 'Research', icon: 'research', appType: 'research', position: { x: 1, y: 1 } },
  { id: 'blog', name: 'Blog', icon: 'notepad', appType: 'blog', position: { x: 1, y: 2 } },
];

// Start menu configuration
export const START_MENU_LEFT_ITEMS: StartMenuItem[] = [
  { id: 'internet-explorer', name: 'Internet Explorer', icon: 'internet-explorer', appType: 'internet-explorer' },
  { id: 'msn-messenger', name: 'MSN Messenger', icon: 'msn-messenger', appType: 'msn-messenger' },
  { id: 'projects', name: 'My Projects', icon: 'folder-projects', appType: 'projects' },
  { id: 'about-me', name: 'About Me', icon: 'user', appType: 'about-me' },
  { id: 'resume', name: 'Resume', icon: 'document', appType: 'resume' },
  { id: 'research', name: 'Research', icon: 'research', appType: 'research' },
  { id: 'blog', name: 'Blog', icon: 'notepad', appType: 'blog' },
  { id: 'contact', name: 'Contact', icon: 'mail', appType: 'contact' },
];

export const START_MENU_RIGHT_ITEMS: StartMenuItem[] = [
  { id: 'my-computer', name: 'My Computer', icon: 'computer', appType: 'my-computer' },
  { id: 'command-prompt', name: 'Command Prompt', icon: 'command-prompt', appType: 'command-prompt' },
  { id: 'minecraft', name: 'Minecraft', icon: 'minecraft', appType: 'minecraft' },
  { id: 'minesweeper', name: 'Minesweeper', icon: 'minesweeper', appType: 'minesweeper' },
  { id: 'solitaire', name: 'Solitaire', icon: 'solitaire', appType: 'solitaire' },
  { id: 'settings', name: 'Settings', icon: 'settings', appType: 'settings' },
];

// Portfolio data - Projects
export const PROJECTS: Project[] = [
  {
    id: 'wattnest',
    title: 'Wattnest - Startup',
    description: 'Plug-and-play smart controllers that retrofit onto existing industrial equipment for real-time monitoring and grid integration.',
    longDescription: 'Wattnest builds plug-and-play smart controllers that retrofit onto existing commercial building equipment (boilers, chillers, RTUs, AHUs) via CTA-2045 or Modbus ports. Over 50% of commercial buildings run outdated equipment lacking modern connectivity—our solution provides real-time energy monitoring, remote scheduling, fault detection, and BMS integration without equipment replacement. Currently in the UC Berkeley SkyDeck accelerator (Pad13 Batch 21) with pilot deployments at UC campus facilities.',
    technologies: ['React', 'TypeScript', 'Python', 'CTA-2045', 'Modbus', 'AWS', 'PostgreSQL'],
    features: [
      'Real-time energy monitoring and analytics',
      'Remote scheduling and setpoint control',
      'Fault detection and diagnostics (FDD)',
      'BMS/EMIS integration via open APIs',
      'Grid demand response participation',
      'Landed cost under $60 per unit at scale',
    ],
    challenges: 'Designing a universal retrofit solution compatible with legacy equipment from multiple manufacturers while maintaining sub-$60 unit economics.',
    liveUrl: 'https://wattnest.net',
    featured: false,
    role: 'Co-Founder & Software Engineer',
    startDate: '2025-04',
    endDate: 'Present',
  },
  {
    id: 'kanteti-me',
    title: 'Kanteti.me',
    description: 'Personal portfolio website built with Next.js and Three.js featuring interactive 3D elements.',
    longDescription: 'An immersive portfolio website featuring a fully interactive Minecraft-style 3D environment built with Three.js. The homepage renders a complete procedurally generated village with 1,500+ ground blocks, 80 floating blocks, 12 houses, and 10 trees with swaying animations. Camera position is driven by scroll, moving through the 3D world as users navigate content. The projects page implements raycasting for interactive 3D signs with click-to-navigate functionality. Animation is handled by Anime.js with Intersection Observer triggers featuring elastic entrance animations and staggered element reveals. Deployed to Cloudflare Workers via @opennextjs/cloudflare for edge-first serverless delivery.',
    technologies: ['Next.js 15', 'React 19', 'TypeScript', 'Three.js', 'Anime.js', 'Tailwind CSS 4'],
    features: [
      'Procedurally generated Minecraft village with 1,500+ blocks',
      'Scroll-driven camera navigation through 3D world',
      'Raycasting for interactive 3D project signs',
      'Elastic/smooth entrance animations with staggered reveals',
      'Canvas-rendered textures for project information',
      'Edge-first deployment on Cloudflare Workers',
    ],
    challenges: 'Optimizing Three.js performance with geometry/material disposal on component unmount, pixel ratio capping at 2x, and prefers-reduced-motion media query support.',
    liveUrl: 'https://kanteti.me',
    githubUrl: 'https://github.com/IJustWantAJob/kanteti-me-website',
    featured: false,
    role: 'Solo Developer',
    startDate: '2024-10',
    endDate: 'Present',
  },
  {
    id: 'parliamentary-assistant',
    title: 'SUA Parliamentary Assistant',
    description: 'Personal website providing AI-powered parliamentary procedure guidance for UCSC Student Union Assembly.',
    longDescription: 'A full-stack AI application built to automate my role as Parliamentarian of the UCSC Student Union Assembly. The chat system uses Vercel AI SDK\'s streamText() with a custom Letta provider for real-time streaming responses. Since Letta manages conversation state server-side, the API only sends the latest user message. The system syncs reference documents from Google Drive to Letta\'s knowledge base via service account authentication, filtering for PDFs and Google Docs with automatic conversion, duplicate checking, and processing status polling. The UI features glassmorphism card effects with backdrop blur, Framer Motion animations, and GitHub Flavored Markdown rendering.',
    technologies: ['Next.js 15', 'TypeScript', 'Tailwind CSS', 'Framer Motion', 'Letta AI', 'Vercel AI SDK', 'Google APIs'],
    features: [
      'Real-time streaming AI responses via Letta provider',
      'Google Drive document sync with duplicate checking',
      'Service account authentication with read-only scope',
      'Glassmorphism UI with backdrop blur effects',
      'GitHub Flavored Markdown rendering',
      'Pre-populated example questions for quick start',
    ],
    challenges: 'Implementing efficient document sync from Google Drive with service account authentication, PDF export from Google Docs, duplicate checking, and processing status polling.',
    liveUrl: 'https://suaparli.org',
    githubUrl: 'https://github.com/IJustWantAJob/Parliamentary_letta_llm',
    featured: true,
    role: 'Solo Developer',
    startDate: '2024-09',
    endDate: 'Present',
  },
  {
    id: 'voxelize-world',
    title: 'Voxelize World',
    description: 'Multiplayer voxel world with procedural skyblock island, real-time block editing, and day/night cycle.',
    longDescription: 'A multiplayer voxel world embedded in a portfolio website, built on the Voxelize engine with a Rust backend. The skyblock island generation runs server-side: a flat-top ellipsoid with radius 40 blocks centered at y=80, layered as Grass → Dirt (3 blocks) → Stone, decorated with 25 trees, 20 bushes, 16 grass patches, 15 mushrooms, and cross-shaped paths with diagonal corners. The TypeScript client (~1,171 lines) handles RigidControls for physics-based movement, VoxelInteract for block placement/destruction, and Peers class for multiplayer character synchronization. Network layer uses Protocol Buffers (gRPC) over WebSocket with transform feedback ping-pong for efficient state sync. Deployed with Cloudflare Pages frontend and AWS Lightsail backend with NGINX reverse proxy.',
    technologies: ['TypeScript', 'Three.js', 'Vite', 'Rust', 'Actix-web', 'Rapier3D', 'Protocol Buffers', 'specs ECS'],
    features: [
      'Procedural skyblock island with grass/dirt/stone layers',
      'Real-time multiplayer via Protocol Buffers over WebSocket',
      '4-phase day/night cycle with dynamic sky colors',
      'Block placement/destruction with VoxelInteract',
      '10-slot hotbar with building materials',
      'First-person and third-person camera modes',
      'Custom respawn system (teleports on void fall)',
      'Chat system and view-only mode toggle',
    ],
    challenges: 'Implementing efficient state sync between Rust backend (Actix-web with specs ECS and Rapier3D physics) and TypeScript frontend using Protocol Buffers over WebSocket with transform feedback ping-pong.',
    liveUrl: 'https://game.kanteti.me',
    githubUrl: 'https://github.com/IJustWantAJob/vox_personal_website',
    featured: true,
    role: 'Solo Developer',
    startDate: '2024-08',
    endDate: 'Present',
  },
  {
    id: 'webgl-playground',
    title: 'WebGL Playground',
    description: 'Interactive WebGL2 educational platform with 18 demonstrations of graphics techniques and real-time parameter adjustment.',
    longDescription: 'An interactive WebGL2 educational platform featuring 18 demonstrations of graphics techniques built with raw WebGL2 (no Three.js). Core utilities abstract common patterns: shader program compilation with error handling and transform feedback setup, VAO management with fullscreen triangle/quad helpers, texture creation and framebuffer management, and FPS/draw call stats tracking. Demos span four categories: Shaders (procedural nebula with FBM noise and domain warping, voronoi patterns with edge detection, raymarching with SDF primitives, soft shadows and AO), Geometry (heightfield wave vertex displacement, barycentric coordinate wireframe without geometry shader), Performance (5,000+ instances via vertexAttribDivisor, 50,000 GPU particles via transform feedback where CPU never touches position data), and Postprocessing (bloom with bright-pass extraction and separable gaussian blur, glitch effects, dithering with Bayer 4x4 and blue noise quantization, color grading with RGB curves and lift/gamma/gain).',
    technologies: ['React 19', 'TypeScript', 'Vite', 'WebGL2', 'GLSL', 'gl-matrix'],
    features: [
      '18 interactive graphics demonstrations across 4 categories',
      'Shader demos: nebula, voronoi, raymarching, SDF shapes, metaballs, water caustics',
      'GPU particle simulation (50,000 particles) via transform feedback',
      'Barycentric coordinate wireframe technique (no geometry shader)',
      'Ping-pong FBO for reaction-diffusion and Game of Life',
      'Real-time parameter adjustment with performance HUD',
      'Lazy-loaded demos with URL sync for sharing configurations',
    ],
    challenges: 'Implementing efficient GPU-side particle simulation where CPU never touches position data using transform feedback with ping-pong buffers, and barycentric coordinate wireframe rendering without geometry shader support.',
    liveUrl: 'https://webgl.kanteti.me',
    githubUrl: 'https://github.com/IJustWantAJob/webgl-test',
    featured: false,
    role: 'Solo Developer',
    startDate: '2024-07',
    endDate: '2024-09',
  },
  {
    id: 'load-shifting',
    title: 'Load Shifting Research',
    description: 'MILP optimization for HPWH load shifting achieving 42-58% cost savings using time-of-use pricing.',
    longDescription: 'Research project extending work from Lawrence Berkeley National Laboratory on grid flexibility, investigating electric load shifting for Heat Pump Water Heaters (HPWHs) as distributed energy resources. The thermal model tracks state-of-charge (SoC) normalized between 0-1 with energy balance per 15-minute interval: SoC[t] = SoC[t-1] × (1 - loss_rate) + COP_hpwh × c_hpwh[t] + COP_res × c_res[t] - draw_demand[t]. Physical parameters: 80-90 gallon tanks (~15.85 kWh thermal capacity), heat pump COP of 3.0 (1.5 kW max), resistance heater COP of 1.0 (4.5 kW max), 1% heat loss per hour, 50% minimum SoC threshold for comfort. Using SCE TOU-D-Prime pricing ($0.54/kWh peak, $0.23/kWh off-peak), single HPWH optimization achieved 42% savings ($1.23 → $0.71) and coordinated 6-household optimization achieved 58.8% savings ($2.77 → $1.62). Builds on ML development for the PG&E/SCE Wattersaver program using DTW, DBA, and TSLearn for HPWH signal clustering with 95% accuracy.',
    technologies: ['Python', 'PuLP', 'GLPK', 'Pandas', 'NumPy', 'Matplotlib', 'NetworkX'],
    features: [
      'Thermal state-of-charge model with energy balance equations',
      'Individual and centralized optimization modes',
      'Real-world hot water draw data from 148 HPWHs',
      'Multiple utility rate plans (PG&E, SCE, SMUD TOU pricing)',
      '15-minute resolution scheduling with 50% SoC comfort floor',
      'Peak load and emissions reduction analysis',
    ],
    challenges: 'Balancing thermal comfort constraints (50% minimum SoC to maintain 50°C) with cost optimization while accounting for variable draw patterns from real-world data.',
    githubUrl: 'https://github.com/IJustWantAJob/loadshifting_self_research',
    featured: true,
    role: 'Researcher',
    startDate: '2024-01',
    endDate: '2024-06',
  },
  {
    id: 'quantum-research',
    title: 'Quantum Computing Optimization',
    description: 'Hybrid quantum-classical optimization for smart grid load scheduling using QAOA and Qiskit.',
    longDescription: 'Research exploring hybrid quantum-classical optimization for smart grid load scheduling using QAOA (Quantum Approximate Optimization Algorithm). The optimization problem formulates 20 HPWHs over 24 hours as 480 decision variables with energy conservation constraints per household, maximum capacity constraints per hour (75 kWh limit), and time-varying electricity prices. The problem is converted to QUBO (Quadratic Unconstrained Binary Optimization) format via penalty method with coefficient 1×10⁶ to ensure constraint violations are heavily penalized. QAOA implementation uses Qiskit\'s Sampler primitive with COBYLA classical optimizer wrapped in MinimumEigenOptimizer. Classical LP baseline achieved 29.75% savings ($358.21 → $251.63). Successfully tested 6-qubit QAOA on reduced problems (2 HPWHs × 3 hours), demonstrating the QUBO conversion and QAOA integration while noting that full 480-variable problems exceed current quantum simulator limits.',
    technologies: ['Qiskit 1.1.0', 'Qiskit-Algorithms', 'Qiskit-Optimization', 'PuLP', 'NumPy', 'COBYLA'],
    features: [
      'QUBO problem formulation via penalty method',
      'QAOA with Sampler primitive and COBYLA optimizer',
      'MinimumEigenOptimizer wrapper for QAOA integration',
      'Classical LP baseline comparison (29.75% savings)',
      'Energy conservation and 75 kWh capacity constraints',
      'Scalability analysis identifying simulator limits',
    ],
    challenges: 'Full 480-variable problem exceeds current quantum simulator limits (~100 variables max). Demonstrated approach on 6-qubit reduced problems (2 HPWHs × 3 hours). Perfect price forecasting assumption unrealistic for production deployment.',
    githubUrl: 'https://github.com/IJustWantAJob/qiskit_practice',
    featured: false,
    role: 'Researcher',
    startDate: '2024-03',
    endDate: '2024-05',
  },
  {
    id: 'usps-regression',
    title: 'USPS Occupancy ML',
    description: 'ML pipeline scraping USPS FOIA data to predict building occupancy dates using Gradient Boosting.',
    longDescription: 'A complete end-to-end machine learning pipeline that processes publicly available USPS facility data from Freedom of Information Act (FOIA) sources to predict building occupancy dates. The custom web scraper bypasses HTTP blocking using browser-mimicking headers, throttles requests at 2-second intervals, and consolidates 50 state-level CSV files. Data cleaning handles inconsistent row structures across files, removes ~90% of irrelevant regional columns, and retains 9 core fields including district, location details, square footage, and occupancy dates. Feature engineering one-hot encodes categorical variables (~22,000 features initially), converts dates to days since 1900 epoch, applies median imputation for missing values, and uses variance thresholding to reduce dimensionality to ~103 features. Model comparison: Linear Regression (MAE 3,094 days, R² 0.217), Random Forest (MAE 2,512 days, R² 0.280), and tuned Gradient Boosting (MAE 2,598 days, R² 0.373 via RandomizedSearchCV hyperparameter optimization).',
    technologies: ['Python', 'scikit-learn', 'Pandas', 'BeautifulSoup', 'requests', 'NumPy', 'seaborn', 'Matplotlib'],
    features: [
      'Custom web scraper with HTTP blocking bypass and rate limiting',
      'Consolidation of 50 state-level CSV files',
      'One-hot encoding reducing 22,000 → 103 features via variance thresholding',
      'Model comparison: Linear Regression, Random Forest, Gradient Boosting',
      'Hyperparameter optimization via RandomizedSearchCV',
      '~19,500 building records with occupancy dates',
    ],
    challenges: 'FOIA data has inherent reporting delays and regional inconsistencies across states. Dimensionality reduction from 22,000 to 103 features was critical for training speed while maintaining predictive power. Limited to available features without construction permit data integration.',
    githubUrl: 'https://github.com/IJustWantAJob/USPS_occupancy_regression',
    featured: false,
    role: 'Developer',
    startDate: '2023-09',
    endDate: '2023-11',
  },
  {
    id: 'windowsxp-portfolio',
    title: 'Windows XP Portfolio',
    description: 'This very website! A fully interactive Windows XP desktop simulation with authentic boot sequence, games, and apps.',
    longDescription: 'A fully interactive Windows XP desktop simulation serving as a portfolio website. Window management uses React Context with useReducer tracking window state (position, size, state, zIndex, isActive). Custom hooks handle drag (useDraggable with mouse/touch and bounds checking) and resize (useResizable with 8-handle resize from edges and corners). The sound system synthesizes effects using Web Audio API oscillators: startup chimes, minimize whoosh (descending frequency sweep 800Hz → 300Hz with exponential decay and noise texture), error beeps, and more. Boot sequence features 3-bar sliding animation with staggered delays, 1.5s loading phase + 1.5s welcome screen, and authentic XP loading bar gradient. The two-column Start menu includes pinned programs, nested All Programs flyout (Accessories, Games, Startup), system locations, and Log Off/Shut Down buttons. Applications include My Computer file browser, Projects portfolio grid, Notepad text editor, Minesweeper/Solitaire games, Internet Explorer mockup, and Recycle Bin. Full accessibility: keyboard navigation (Tab, Enter, Escape, Arrow keys), ARIA labels/roles, touch support, and reduced motion support.',
    technologies: ['React 18', 'TypeScript', 'Vite', 'CSS Modules', 'Web Audio API'],
    features: [
      'Authentic XP boot sequence with 3-bar animation',
      'Window management with React Context and useReducer',
      'Custom useDraggable and useResizable hooks (no dependencies)',
      'Synthesized sound effects via Web Audio API oscillators',
      'Two-column Start menu with nested All Programs flyout',
      'Classic games: Minesweeper and Solitaire',
      'CRT monitor effect with multiple wallpaper options',
      'Full keyboard navigation, ARIA labels, and touch support',
    ],
    challenges: 'Recreating authentic Windows XP experience in the browser while maintaining modern web standards. Custom drag/resize hooks without external dependencies. Web Audio API oscillator synthesis for authentic sound effects. Accessibility (keyboard navigation, ARIA, touch) without compromising the retro aesthetic.',
    githubUrl: 'https://github.com/IJustWantAJob/windowsxp-site',
    featured: false,
    role: 'Solo Developer',
    startDate: '2024-12',
    endDate: 'Present',
  },
  {
    id: 'photo-sorter',
    title: 'Photo Sorter',
    description: 'Local web app for browsing and sorting macOS Photos library by file size to find large files for cleanup.',
    longDescription: 'A locally-hosted web application for browsing and sorting your macOS Photos library by file size. The macOS Photos app doesn\'t let you sort by file size, and since the Photos library is stored as an opaque .photoslibrary package, you can\'t easily browse files by size in Finder. This tool solves that problem. Built with FastAPI backend and vanilla HTML/CSS/JavaScript frontend with no build step. Uses the osxphotos library to read the Photos database (read-only), generates thumbnails on-the-fly with Pillow, and performs delete/export/reveal actions via AppleScript to interact with Photos.app. Features include sorting by size, filtering by media type (photos/videos), size thresholds (1MB, 10MB, 100MB+), iCloud status indicators, and dark theme.',
    technologies: ['Python', 'FastAPI', 'osxphotos', 'Pillow', 'AppleScript', 'HTML/CSS/JavaScript'],
    features: [
      'Sort by file size to find largest photos and videos',
      'Filter by media type (all, photos only, videos only)',
      'Size thresholds (1MB, 10MB, 100MB+)',
      'iCloud status indicators for cloud-only files',
      'Delete/export/reveal actions via AppleScript',
      '100% local - no data leaves your machine',
      'Dark theme with responsive design',
    ],
    challenges: 'Working with the opaque .photoslibrary package structure. Reading Photos database via osxphotos without modifying it. Implementing thumbnail generation on-the-fly for performance. Handling iCloud-only files that aren\'t downloaded locally.',
    githubUrl: 'https://github.com/IJustWantAJob/photo_sorter',
    featured: false,
    role: 'Solo Developer',
    startDate: '2024-11',
    endDate: '2024-12',
  },
];

// Blog posts
export const BLOG_POSTS: BlogPost[] = [
  {
    id: 'email-outreach-platform',
    title: 'How We Automated Our Entire Outreach Pipeline',
    date: '2026-02-28',
    excerpt: 'We built a system that sends personalized email campaigns, tracks replies, follows up automatically, and even finds new leads while we sleep.',
    content: 'Manual outreach was eating all our time. So we built our own system to handle it.',
    tags: ['Automation', 'AI', 'Outreach'],
    markdownFile: '/blog/email-outreach-platform.md',
    aliases: ['automation', 'outreach', 'email-outreach'],
  },
  {
    id: 'linkedin-outreach-engine',
    title: 'How We Built a LinkedIn Outreach System with AI',
    date: '2026-03-10',
    excerpt: 'A system that finds leads, writes personalized messages with AI, and never sends anything without our approval. 42% acceptance rate, zero restrictions.',
    content: 'LinkedIn is where our buyers live. We built a system to handle the repetitive parts of outreach.',
    tags: ['Automation', 'AI', 'LinkedIn'],
    markdownFile: '/blog/linkedin-outreach-engine.md',
    upcoming: true,
    aliases: ['linkedin', 'linkedin-outreach'],
  },
  {
    id: 'grid-cybersecurity-research',
    title: 'What If Someone Hacks California\'s Virtual Power Plants?',
    date: '2026-03-17',
    excerpt: 'California has 20,000+ MW of remotely controllable devices on the grid. Most were not built with security in mind. We modeled what happens when they get hacked.',
    content: 'I spent months researching what happens if someone coordinates an attack on California\'s distributed energy resources.',
    tags: ['Cybersecurity', 'Energy', 'Research'],
    markdownFile: '/blog/grid-cybersecurity-research.md',
    upcoming: true,
    aliases: ['cybersecurity', 'grid-security', 'vpp-security'],
  },
];

// Skills
export const SKILLS: Skill[] = [
  // Languages
  { name: 'Python', level: 92, category: 'backend' },
  { name: 'TypeScript', level: 90, category: 'frontend' },
  { name: 'JavaScript', level: 92, category: 'frontend' },
  { name: 'Scala', level: 75, category: 'backend' },
  { name: 'C#/.NET', level: 80, category: 'backend' },
  { name: 'Rust', level: 70, category: 'backend' },
  { name: 'SQL', level: 85, category: 'backend' },
  // Frontend
  { name: 'React', level: 95, category: 'frontend' },
  { name: 'Next.js', level: 90, category: 'frontend' },
  { name: 'Three.js', level: 85, category: 'frontend' },
  { name: 'WebGL', level: 80, category: 'frontend' },
  { name: 'Tailwind CSS', level: 90, category: 'frontend' },
  // ML/Data
  { name: 'PyTorch', level: 80, category: 'ml' },
  { name: 'scikit-learn', level: 88, category: 'ml' },
  { name: 'TSLearn', level: 85, category: 'ml' },
  { name: 'Apache Spark', level: 75, category: 'ml' },
  { name: 'BigQuery', level: 80, category: 'ml' },
  // Tools & Infrastructure
  { name: 'AWS', level: 75, category: 'tools' },
  { name: 'Google Cloud', level: 80, category: 'tools' },
  { name: 'Docker', level: 80, category: 'tools' },
  { name: 'Vercel', level: 88, category: 'tools' },
  { name: 'Git', level: 92, category: 'tools' },
];

// About info
export const ABOUT_INFO = {
  name: 'Aditya Kanteti',
  title: 'PM/Software Engineer',
  bio: `Hi! I'm Adi, a senior at UC Santa Cruz studying Computer Engineering. I like building cool things (like this) and constantly try to learn about new technologies, tools, and tricks to stay at the forefront of technology.

When I'm not coding, I'm working on Wattnest with friends (currently part of SkyDeck pad-13 b21) and doing research around algorithms, distributed energy resources, and demand flexibility.`,
  email: 'adityakanteti@yahoo.com',
  location: 'Santa Cruz / Berkeley, California',
  avatar: '/avatar.jpg',
  github: 'IJustWantAJob',
  twitter: 'AdiCodeMonkey',
  linkedin: 'Adityakanteti',
};

// Wallpapers
export const WALLPAPERS = [
  { id: 'bliss', name: 'Bliss', url: '/wallpapers/bliss.jpg' },
  { id: 'azure', name: 'Azure', url: '/wallpapers/azure.svg' },
  { id: 'sunset', name: 'Sunset', url: '/wallpapers/sunset.svg' },
  { id: 'forest', name: 'Forest', url: '/wallpapers/forest.svg' },
  { id: 'ocean', name: 'Ocean', url: '/wallpapers/ocean.svg' },
  { id: 'purple-haze', name: 'Purple Haze', url: '/wallpapers/purple-haze.svg' },
  { id: 'minimal-dark', name: 'Dark Mode', url: '/wallpapers/minimal-dark.svg' },
  { id: 'olive-green', name: 'Olive Green', url: '/wallpapers/olive-green.svg' },
  { id: 'silver', name: 'Silver', url: '/wallpapers/silver.svg' },
  { id: 'solid-blue', name: 'Solid Blue', url: 'none' },
];
